import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { useChat } from '../hooks/useChat';
import { MESSAGE_MAX, setReaction, CHAT_IMAGE, getMessageImage } from '../firebase/chatService';
import { compressChatImage } from '../utils/imageCompress';
import EmojiPicker from './EmojiPicker';
import PageSpinner from './PageSpinner';
import ChatMessage from './ChatMessage';
import './ChatThread.css';
import PersonIcon from './PersonIcon';

// Time only for today, date for anything older — a thread is mostly read
// "today", and a full date on every line is noise.
function stamp(ts, locale) {
  const d = ts?.toDate?.();
  if (!d) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

// ── CHAT THREAD ──────────────────────────────────────────────────────────────
// One conversation: the messages, and the box to add to them.
//
// All the Firestore work is in useChat — this component only renders what it
// is given, so there is no listener here to leak.
export default function ChatThread({ chatId, other, onBack }) {
  const { user } = useAuth();
  const { t, lang } = useT();
  const {
    messages, loading, sending, send, edit, remove,
    loadOlder, loadingOlder, canLoadOlder,
  } = useChat(chatId, other?.uid);

  const [draft, setDraft] = useState('');
  // A picture chosen and already encoded, waiting to be sent with (or without)
  // a caption. Null when there is none.
  const [pending, setPending] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [imgError, setImgError] = useState('');
  // A picture opened full-screen: { id, url, loading }.
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);
  // The message being replied to, or null. Held as the whole message so the
  // composer can show a preview without another lookup.
  const [replyTo, setReplyTo] = useState(null);
  // Which message is being edited, and its working copy.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  // Deleting is irreversible for both sides, so it asks first.
  const [confirmId, setConfirmId] = useState(null);
  const inputRef = useRef(null);

  // Inserts at the CARET, not at the end.
  //
  // Appending is the easy version and it is wrong the moment someone goes back
  // to add a smiley mid-sentence — the emoji lands after the full stop instead
  // of where they were typing. Selected text is replaced, which is what every
  // other text field does.
  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    const next = (draft.slice(0, start) + emoji + draft.slice(end)).slice(0, MESSAGE_MAX);
    setDraft(next);
    // After React has repainted, put the caret after what was just inserted and
    // hand focus back — otherwise the next keystroke goes nowhere.
    requestAnimationFrame(() => {
      if (!el) return;
      const caret = Math.min(start + emoji.length, next.length);
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const scrollerRef = useRef(null);
  // Height before an "older" page is prepended, so the view can be restored to
  // the same message instead of jumping to the top.
  const anchorRef = useRef(null);
  // False until this thread has been snapped to its newest message once.
  const landedRef = useRef(false);
  // Set by submit(), so the message I just sent scrolls into view even if I had
  // scrolled up to read history a moment earlier.
  const forceBottomRef = useRef(false);

  // Jumps the MESSAGE LIST to the bottom — and nothing else.
  //
  // Not scrollIntoView: that walks up the ancestor chain and scrolls every
  // scrollable parent it finds, which on a phone drags the whole page around
  // the moment the keyboard resizes the viewport. Setting scrollTop moves this
  // one element and leaves the page where it is.
  const jumpToBottom = () => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // Restoring position after an older page was prepended.
    if (anchorRef.current != null) {
      el.scrollTop = el.scrollHeight - anchorRef.current;
      anchorRef.current = null;
      return;
    }

    // OPENING THE THREAD: land on the newest message.
    //
    // This used to be an effect on [chatId], which fired while `messages` was
    // still empty — there was nothing to scroll past yet, so it did nothing,
    // and the list then rendered its first page sitting at scrollTop 0, i.e.
    // at the OLDEST message. The jump has to happen on the render that first
    // has messages in it, which is here.
    if (!landedRef.current) {
      if (!messages.length) return;
      landedRef.current = true;
      jumpToBottom();
      return;
    }

    // Afterwards, follow the conversation down only when already near the
    // bottom, so reading history isn't yanked away by someone else's message.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (forceBottomRef.current || nearBottom) {
      forceBottomRef.current = false;
      jumpToBottom();
    }
  }, [messages]);

  // Switching threads starts the "land on newest" logic over.
  useEffect(() => {
    landedRef.current = false;
    forceBottomRef.current = false;
    setEditingId(null);
    setConfirmId(null);
  }, [chatId]);

  // Quoted messages are looked up by id — the reply carries only the id, so
  // the text always comes from the real document.
  const byId = useMemo(() => new Map(messages.map(m => [m.id, m])), [messages]);

  // Toggle: choosing the reaction you already have removes it. One control for
  // both directions, from the picker and from the chips under the bubble.
  const react = useCallback((m, emoji) => {
    if (!chatId || !user?.uid) return;
    const current = (m.reactions || {})[user.uid];
    setReaction(chatId, m.id, user.uid, current === emoji ? null : emoji)
      .catch(err => console.warn('[chat] reaction failed:', err?.code || err));
  }, [chatId, user?.uid]);

  // ── ATTACHING A PICTURE ────────────────────────────────────────────────────
  // Encoded as soon as it is chosen, not at send time: a phone photo takes a
  // moment to resize, and doing that after the tap would leave the composer
  // apparently dead. It also means the failure — a file too large to survive
  // compression, or not an image at all — is reported while the user is still
  // looking at the picker, rather than swallowing a send.
  async function pickImage(e) {
    const file = e.target.files?.[0];
    // Cleared immediately so choosing the SAME file twice fires `change` again.
    e.target.value = '';
    if (!file) return;
    setImgError('');
    setAttaching(true);
    try {
      setPending(await compressChatImage(file, CHAT_IMAGE));
    } catch (err) {
      setImgError(err?.message === 'not-an-image' ? t('chat_img_type') : t('chat_img_large'));
    } finally {
      setAttaching(false);
    }
  }

  // ── OPENING A PICTURE ──────────────────────────────────────────────────────
  // The preview shows immediately — it is already on screen — and the full
  // rendition replaces it when it arrives. One document read, and only for the
  // picture actually opened; a thread of fifty photos costs nothing here until
  // someone taps one.
  //
  // Keyed by message id so a slow fetch cannot land on a different picture the
  // user has since opened, or on a closed viewer.
  const openImage = useCallback(async (m) => {
    if (!m?.image?.url) return;
    setViewing({ id: m.id, url: m.image.url, loading: !!m.image.full });
    if (!m.image.full) return;
    const full = await getMessageImage(chatId, m.id);
    setViewing(v => (v && v.id === m.id ? { ...v, url: full || v.url, loading: false } : v));
  }, [chatId]);

  useEffect(() => {
    if (!viewing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setViewing(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewing]);

  async function submit(e) {
    e.preventDefault();
    const body = draft.trim();
    // An image on its own is a message; text on its own still is too.
    if ((!body && !pending) || sending || attaching) return;
    const quoted = replyTo;
    const picture = pending;
    setDraft('');            // clear first; the listener echoes it straight back
    setReplyTo(null);
    setPending(null);
    setImgError('');
    forceBottomRef.current = true;

    // KEEP THE KEYBOARD UP.
    //
    // On a phone the send button steals focus from the textarea, the keyboard
    // slides away, and the viewport resize that follows shifts the whole thread.
    // Focus is handed straight back — synchronously, while the tap is still the
    // active user gesture, because iOS will not re-open the keyboard from a
    // focus() call that happens after an await.
    inputRef.current?.focus();

    try {
      await send(body, {
        ...(quoted ? { replyTo: quoted.id } : null),
        ...(picture ? { image: picture } : null),
      });
    } catch (err) {
      // ── THE MESSAGE WAS REFUSED ─────────────────────────────────────────────
      // GIVE THE SENTENCE BACK. The draft is cleared optimistically above,
      // before the send, because the listener normally echoes the message
      // straight back and clearing after would leave the box full for a beat.
      // When the send is REFUSED there is no echo — so without this the reader
      // watches a paragraph they just typed disappear, with no way to recover
      // it and no idea why.
      //
      // The reply target and the attached picture come back with it: they were
      // part of the same unsent message.
      setDraft(body);
      if (quoted) setReplyTo(quoted);
      if (picture) setPending(picture);

      // Only profanity gets a specific line; anything else keeps the generic
      // failure, because guessing at an unknown error is how a network blip
      // ends up telling somebody they swore.
      setImgError(err?.code === 'profanity'
        ? t('chat_blocked_word').replace('{word}', err.word || '')
        : t('chat_send_failed'));
    }
    // The send resolved on some other tick; if anything took focus in between,
    // take it back.
    inputRef.current?.focus();
  }

  function cancelEdit() { setEditingId(null); setEditDraft(''); }

  async function saveEdit(e) {
    e.preventDefault();
    const body = editDraft.trim();
    const id = editingId;
    if (!body || !id) return;
    cancelEdit();          // close first; the listener echoes the new text back
    await edit(id, body);
  }

  async function confirmDelete() {
    const id = confirmId;
    setConfirmId(null);
    if (id) await remove(id);
  }

  function onOlder() {
    anchorRef.current = scrollerRef.current
      ? scrollerRef.current.scrollHeight - scrollerRef.current.scrollTop
      : null;
    loadOlder();
  }

  if (!chatId) {
    return <div className="ct ct--empty"><p className="ct-hint">{t('chat_pick')}</p></div>;
  }

  return (
    <div className="ct">
      <header className="ct-head">
        {onBack && (
          <button type="button" className="ct-back" onClick={onBack} aria-label={t('back')}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <Link to={`/designer/${other?.uid}`} className="ct-who">
          <span className="ct-avatar">
            {other?.avatar ? <img src={other.avatar} alt="" /> : <PersonIcon />}
          </span>
          <span className="ct-name">{other?.name || t('chat_someone')}</span>
        </Link>
      </header>

      <div className="ct-scroll" ref={scrollerRef}>
        {canLoadOlder && (
          <button type="button" className="ct-older" onClick={onOlder} disabled={loadingOlder}>
            {loadingOlder ? t('chat_loading') : t('chat_older')}
          </button>
        )}

        {loading ? (
          <PageSpinner label={t('chat_loading')} />
        ) : messages.length === 0 ? (
          <p className="ct-hint">{t('chat_empty')}</p>
        ) : (
          <ul className="ct-list">
            {messages.map(m => {
              const mine = m.senderUid === user?.uid;

              // Editing happens in place, in the bubble — a modal for one line
              // of text would be heavier than the thing being changed.
              if (mine && editingId === m.id) {
                return (
                  <li key={m.id} className="ct-msg ct-msg--mine">
                    <form className="ct-edit" onSubmit={saveEdit}>
                      <textarea
                        className="ct-edit-input"
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value.slice(0, MESSAGE_MAX))}
                        rows={2}
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(e); }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <div className="ct-edit-row">
                        <button type="button" className="ct-edit-btn" onClick={cancelEdit}>
                          {t('cancel')}
                        </button>
                        <button type="submit" className="ct-edit-btn ct-edit-btn--save"
                          disabled={!editDraft.trim()}>
                          {t('chat_save')}
                        </button>
                      </div>
                    </form>
                  </li>
                );
              }

              return (
                <ChatMessage
                  key={m.id}
                  m={{ ...m, stamp: stamp(m.createdAt, lang) }}
                  mine={mine}
                  myUid={user?.uid}
                  chatId={chatId}
                  // Resolved HERE, from the loaded thread, because the reply
                  // stores only an id — see sendMessage. `undefined` means the
                  // quoted message is outside the loaded window (or deleted),
                  // which the bubble shows as unavailable.
                  quoted={m.replyTo ? byId.get(m.replyTo) : undefined}
                  onReply={setReplyTo}
                  onEdit={(msg) => { setEditingId(msg.id); setEditDraft(msg.text); }}
                  onDelete={(msg) => setConfirmId(msg.id)}
                  onReact={react}
                  onOpenImage={openImage}
                />
              );
            })}
          </ul>
        )}
      </div>

      {confirmId && (
        <div className="ct-confirm" role="dialog" aria-modal="true">
          <div className="ct-confirm-box">
            <p className="ct-confirm-text">{t('chat_delete_msg_confirm')}</p>
            <div className="ct-confirm-row">
              <button type="button" className="ct-edit-btn" onClick={() => setConfirmId(null)}>
                {t('cancel')}
              </button>
              <button type="button" className="ct-edit-btn ct-edit-btn--danger" onClick={confirmDelete}>
                {t('delete_tooltip')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WHAT YOU ARE REPLYING TO, above the composer. Without it the reply
          context is invisible until after the message is sent, and a swipe
          that armed by accident would be indistinguishable from one that did
          not. The ✕ is how you back out. */}
      {replyTo && (
        <div className="ct-replying">
          <span className="ct-replying-body">
            <span className="ct-replying-label">{t('chat_replying_to')}</span>
            <span className="ct-replying-text">{replyTo.text}</span>
          </span>
          <button
            type="button"
            className="ct-replying-x"
            onClick={() => setReplyTo(null)}
            aria-label={t('cancel')}
          >
            ✕
          </button>
        </div>
      )}

      {/* THE PICTURE ABOUT TO BE SENT. Shown before it goes, for the same
          reason the reply preview is: an attachment you cannot see until after
          you have sent it is one you cannot check or take back. */}
      {(pending || attaching || imgError) && (
        <div className="ct-attach">
          {attaching && <span className="ct-attach-busy">{t('chat_img_working')}</span>}
          {imgError && <span className="ct-attach-err">{imgError}</span>}
          {pending && (
            <>
              <img className="ct-attach-thumb" src={pending.previewUrl} alt="" />
              <span className="ct-attach-size">
                {Math.round(pending.previewBytes / 1024)} KB
              </span>
              <button
                type="button"
                className="ct-attach-x"
                onClick={() => { setPending(null); setImgError(''); }}
                aria-label={t('cancel')}
              >✕</button>
            </>
          )}
        </div>
      )}

      <form className="ct-form" onSubmit={submit}>
        {/* Hidden, and driven by the button beside it — a bare file input
            cannot be styled to match anything. */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="ct-file"
          onChange={pickImage}
        />
        <button
          type="button"
          className="ct-attach-btn"
          onClick={() => fileRef.current?.click()}
          disabled={sending || attaching}
          aria-label={t('chat_img_add')}
          title={t('chat_img_add')}
          onMouseDown={e => e.preventDefault()}
        >
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2.5" />
            <circle cx="8.5" cy="8.5" r="1.6" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </button>

        {/* Desktop only — phone keyboards already have an emoji key. */}
        <EmojiPicker disabled={sending} onPick={insertEmoji} />
        <textarea
          ref={inputRef}
          className="ct-input"
          value={draft}
          onChange={e => setDraft(e.target.value.slice(0, MESSAGE_MAX))}
          placeholder={t('chat_placeholder')}
          rows={1}
          // NOT disabled while sending. Disabling the focused element blurs it,
          // and a blur is what closes the keyboard — so every message sent on a
          // phone dismissed the keyboard and reflowed the thread. A send takes a
          // moment and typing the next message during it is fine; the send
          // button alone is enough to stop a double submit.
          // Enter sends, Shift+Enter breaks the line — the convention in every
          // messenger, and the reason this is a textarea rather than an input.
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
        />
        <button
          type="submit"
          className="ct-send"
          disabled={sending || attaching || (!draft.trim() && !pending)}
          // Pressing a button moves focus to it by default, which blurs the
          // textarea and closes the keyboard before submit() ever runs.
          // Cancelling the default on mousedown leaves focus where it is; the
          // click still fires normally.
          //
          // mousedown ONLY — never touchstart. Cancelling a touchstart default
          // suppresses the compatibility click that follows it on iOS, which
          // would stop the button sending anything at all.
          onMouseDown={e => e.preventDefault()}
        >
          {sending ? '…' : t('chat_send')}
        </button>
      </form>

      {/* A picture, full size. Deliberately its own small viewer rather than
          the gallery lightbox: that one is built around a list of items to page
          through and a social bar, neither of which a chat picture has. */}
      {viewing && (
        <div className="ct-viewer" role="dialog" aria-modal="true" onClick={() => setViewing(null)}>
          <button
            type="button"
            className="ct-viewer-x"
            onClick={() => setViewing(null)}
            aria-label={t('cancel')}
          >✕</button>
          <img
            className={`ct-viewer-img${viewing.loading ? ' ct-viewer-img--loading' : ''}`}
            src={viewing.url}
            alt=""
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
